// store/profiles.ts
import { defineStore } from "pinia";
import ProfileService from "@/services/ProfileService";
import { useSettingStore } from "./SettingStore"
import { useLoggerStore } from "./LoggerStore"
import type { ProfileInterface } from "@/interfaces";
import type { LookupServiceProviderType } from "@/types";
import { profilesLogger } from "@/utils/loggers";
import ProfileFactory from "@/factories/ProfileFactory";
import { setOperatorIdentity, setKnownCallsigns } from "@/utils/sentry";

export const useProfileStore = defineStore("profiles", {
  state: () => ({
    firstTimeSetup: false,
    profiles: [] as ProfileInterface[],
    activeProfile: undefined as ProfileInterface | undefined,
    editingProfile: undefined as ProfileInterface | undefined,
  }),
  actions: {
    async fetchProfiles() {
      profilesLogger("Fetching profiles...")
      try {
        const profiles = await ProfileService.findAll();
        this.profiles = profiles;
        // Record every callsign on the account so support sees them together.
        // Guard against malformed/legacy profiles (missing operator or callsign)
        // so a bad row can't throw here and abort the rest of fetchProfiles.
        setKnownCallsigns(
          profiles
            .map((profile) => profile.operator?.callsign)
            .filter((callsign): callsign is string => !!callsign)
        );
        profilesLogger(`Profiles fetched: ${profiles.length}`);
        this.firstTimeSetup = this.profiles.length === 0;
      } catch (error) {
        console.error("Error fetching profiles:", error);
      }
    },
    async createProfile() {
      if (!this.editingProfile) { return; }
      try {
        const createdProfile = await ProfileService.create(this.editingProfile);
        profilesLogger('Created Profile:', createdProfile.operator.callsign);
        // Option 1: Refetch profiles after creation
        await this.fetchProfiles()
        this.editingProfile = undefined;
        return createdProfile;
      } catch (error) {
        console.error("Error creating profile:", error);
        throw error; // Re-throw to allow caller to handle
      }
    },
    async activateProfile(profile: ProfileInterface) {
      const activeProfile = this.profiles.find((prof) => prof._id === profile._id)
      if (activeProfile) {
        this.activeProfile = activeProfile;
        // Guard against malformed/legacy profiles missing operator/callsign so
        // activation can't crash; operator-independent settings still apply.
        const callsign = activeProfile.operator?.callsign;
        profilesLogger("Activating Profile:", callsign ?? "(no callsign)");
        useSettingStore().setLookupProvider(activeProfile.lookupService);
        useSettingStore().setActiveProfileID(activeProfile._id);
        if (callsign) {
          useLoggerStore().setOperator(callsign);
        } else {
          // Clear the logger operator so a previously active profile's callsign
          // can't linger as stale attribution on new QSOs.
          console.warn(`activateProfile: profile ${activeProfile._id} has no operator callsign`);
          useLoggerStore().setOperator("");
        }
        // Keep Sentry identity tied to the active profile even if callsign is
        // missing (setOperatorIdentity accepts an undefined callsign).
        setOperatorIdentity(callsign, activeProfile._id);
      }
    },
    async initNewProfile() {
      profilesLogger('Initializing New Profile');
      this.editingProfile = ProfileFactory.newProfile();
    },
    async updateProfile() {
      if (!this.editingProfile) { return; }
      try {
        const updatedProfile = await ProfileService.update(this.editingProfile);
        profilesLogger("Updated Profile:", updatedProfile.operator.callsign);
        await this.fetchProfiles();

        // If they edit the active profile then we activate the profile again
        // in case they've changed something about their callsign lookup
        // service.
        if (this.activeProfile?._id === updatedProfile._id) {
          this.activateProfile(updatedProfile);
        }
        this.editingProfile = undefined;
        return updatedProfile;
      } catch (error) {
        console.error("Error updating profile", error);
        throw error;
      }
    },
    // Persist a lookup-provider change made from the quick-select (slide-out
    // settings panel) onto the active profile, so it survives reloads just like
    // the profile editor's "Default Lookup Service" does. Kept lightweight: no
    // full refetch, and a no-op when the value is unchanged -- so it never fires
    // a redundant write on launch (activateProfile -> setLookupProvider does not
    // call this).
    async persistActiveProfileLookupService(provider: LookupServiceProviderType) {
      if (!this.activeProfile) return;
      if (this.activeProfile.lookupService === provider) return;
      try {
        const updated = await ProfileService.update({
          ...this.activeProfile,
          lookupService: provider,
        });
        this.activeProfile = updated;
        const index = this.profiles.findIndex(
          (profile) => profile._id === updated._id
        );
        if (index !== -1) {
          this.profiles[index] = updated;
        }
        profilesLogger(`Persisted lookupService -> ${provider}`);
      } catch (error) {
        console.error("Error persisting lookup provider", error);
      }
    },
    async deleteProfile(profileId: string) {
      try {
        const deletedProfile = await ProfileService.delete(profileId);
        profilesLogger("Deleted Profile:", deletedProfile?.operator?.callsign ?? profileId);
        await this.fetchProfiles();
        return deletedProfile;
      } catch (error) {
        console.error("Error deleting profile", error);
        throw error;
      }
    }
  },
  getters: {
    findOne: (state) => {
      return (profileId: string) => state.profiles.find((profile) => profile._id === profileId)
    }
  }
});
